import React, { useEffect, useState } from "react";
import {
    TextField,
    Select,
    Grid,
    MenuItem,
    Box,
    LinearProgress,
    Typography,
    Paper,
    InputAdornment,
    useTheme,
    alpha,
    Fade
} from "@mui/material";
import * as dayjs from "dayjs";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import NumbersIcon from "@mui/icons-material/Numbers";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import { API } from "aws-amplify";
import {
    createChildFilterObject,
    createHeaders,
    createRows,
    evaluateField,
    groupBy,
} from "../../../methods";
import { Operators } from "../../../metadata";
import RangeInput from "../RangeInput";
import Search from "../../Search";
const { v4: uuidv4 } = require('uuid');
import { GetDistinctColumnValues } from "../../../services";

const isWeekend = (date) => {
    const day = date.day();

    return day === 0 || day === 6;
};

const ChildFilterWrapper = ({ filter, setFilters, parentValue, selectedColumn, selectionChangedHandler, selectionModel, setSelectionModel, gridSelection, setGridSelection, endingLabel, resultType, metadata, currentParentFilter, validateDate, modalOpen }) => {
    const theme = useTheme();
    const [tableData, setTableData] = useState({
        rows: [],
    });
    const [searchValue, setSearchValue] = useState();
    const [nextData, setNextData] = useState([]);
    const [count, setCount] = useState(0);
    const [headers, setHeaders] = useState([]);
    const [page, setPage] = useState(1);
    const [date, setDate] = useState(null); // for DateRange
    const [dateRange, setDateRange] = useState([null, null]); //for DateRangePicker
    const [inputValue, setInputValue] = useState("");
    const [operatorValue, setOperatorValue] = useState(
        filter?.key === "BIRTH_YEAR" ? "between" : (filter?.operator || "=")
    );
    const [loading, setLoading] = useState(false);
    const [selectedModel, setSelectedModel] = useState({});
    const [initialLoad, setInitialLoad] = useState(true);

    useEffect(() => {
        if (modalOpen) {
            setInitialLoad(true);
        }
    }, [modalOpen, initialLoad]);

    useEffect(() => {
        if (initialLoad && modalOpen) {
            getDistinctColumnValues('', true, 1);
        }
    }, [modalOpen]);


    const getDistinctColumnValues = async (searchText = '', usePaging = false, page, filterType = 'contains') => {
        const jsonBody = filter.queryBuilderJSON[0].distinctValueJSON(
            parentValue,
            searchText
        );
        jsonBody.page = usePaging ? page : 1;
        jsonBody.filterType = filterType;
        jsonBody.db = metadata.db;

        // Find the parent row that matches the current parentValue

        if (currentParentFilter.rows && currentParentFilter.rows.length > 0) {
            const parentRow = currentParentFilter.rows.find(row => {
                if (typeof row.value === 'string' && typeof parentValue === 'string') {
                    const match = row.value.toLowerCase() === parentValue.toLowerCase() || parentValue === "*";
                    return match;
                }
                const match = row.value === parentValue || parentValue === "*";
                return match;
            });

            if (parentRow && parentRow.field) {
                // Use the actual field and operator from the parent row instead of the default metadata
                if (jsonBody.wheres && jsonBody.wheres.rules && jsonBody.wheres.rules.length > 0) {
                    jsonBody.wheres.rules.forEach((rule, index) => {
                        // Replace the default column with the actual selected column from parent row
                        if (rule.field === currentParentFilter.column) {
                            rule.field = parentRow.field;
                        }

                        // Update the operator to match the parent row's current operator
                        if (parentRow.operator && parentRow.operator !== rule.operator) {
                            rule.operator = parentRow.operator;
                        }

                    });
                }
            }
        }

        if (currentParentFilter.rows.some(x => x.operator === 'contains')) {
            jsonBody.wheres?.rules?.forEach((rule, index) => {
                rule.operator = 'contains';
            });
        }

        const body = jsonBody;

        let data = [];
        let count = 0;

        setLoading(true);

        const result = await GetDistinctColumnValues({ body }, null, null);

        let newResults;

        if (result.success === true) {
            const apiRes = JSON.parse(result.data);
            count = apiRes.rowCount;
            data = JSON.parse(result.data).response;
            setLoading(false);
            newResults = { data, count };
        }

        if (newResults?.data) {
            const createdRows = createRows(newResults.data, page);
            const tableHeaders = createHeaders(newResults.data);

            if (page === 1) {
                setTableData({
                    rows: createdRows,
                });
                setHeaders(tableHeaders);
                setCount(newResults.count);
            }
            else {
                setNextData([...nextData, ...createdRows]);
            }
            if (!Object.prototype.hasOwnProperty.call(currentParentFilter, 'rows')) {
                setSelectedModel({});
                setSelectionModel([]);
            } else {
                setSelectedModel(selectionModel);
            }
        }
        setInitialLoad(false);
    }

    const onRowsSelectionHandler = (selectionModel, key) => {
        const ids = selectionModel.type === 'include' ? [...selectionModel.ids] : [];
        if (ids.length < 1) {
            setSelectedModel(prevState => ({
                ...prevState,
                [key]: []
            }));
            // Clear from gridSelection as well
            setGridSelection((prevState) => {
                const newSelection = { ...prevState };
                delete newSelection[key];
                return newSelection;
            });
            return null;
        }

        const selectedRowsData = ids.map((id) => {
            const result = tableData?.rows?.find((row) => row.id === id);

            if (result) {
                const dataKey = Object.keys(result)[0];

                const childFilter = selectedColumn?.childFilters?.find(
                    (f) => f.key === dataKey
                );

                if (childFilter) {
                    const obj = {
                        id: uuidv4(),
                        field: childFilter.column || dataKey,
                        value: result[dataKey],
                        friendlyName: childFilter?.friendlyName,
                        dataType: childFilter.dataType,
                        operator: childFilter.operator || "="
                    };
                    return { ...result, key: dataKey, ...obj };
                } else {
                    // Create a basic filter object even if no child filter definition exists
                    const obj = {
                        id: uuidv4(),
                        field: dataKey,
                        value: result[dataKey],
                        friendlyName: dataKey,
                        dataType: "TEXT",
                        operator: "="
                    };
                    return { ...result, key: dataKey, ...obj };
                }
            }
        });

        const groupedSelectedRows = groupBy(selectedRowsData.filter(Boolean), "key");

        selectionChangedHandler(filter?.key, groupedSelectedRows);

        setSelectionModel((prevState) => {
            const newState = { ...prevState, [key]: ids };
            return newState;
        });
        setSelectedModel((prevState) => {
            const newState = { ...prevState, [key]: ids };
            return newState;
        });
    };

    const handleInputChange = (value) => {
        setInputValue(value);
        setGridSelection((prevState) => {
            const obj = createChildFilterObject(filter, value, operatorValue);
            return { ...prevState, ...obj };
        });
    };

    const handleOperatorChange = (val, dataType) => {
        setOperatorValue(val);
        let obj;
        setGridSelection((prevState) => {
            if (filter?.key === "BIRTH_YEAR") {
                if (val === 'between') {
                    const parts = inputValue.split(',');
                    if (parts.length === 2 && parts[0] && parts[1]) {
                        obj = createChildFilterObject(filter, convertYearRangeToDateString(parts[0], parts[1]), val);
                    } else {
                        obj = createChildFilterObject(filter, inputValue, val);
                    }
                } else {
                    const dateValue = inputValue && inputValue.length === 4 ? convertYearToDateString(inputValue) : inputValue;
                    obj = createChildFilterObject(filter, dateValue, val);
                }
            } else if (dataType === 'DATE') {
                if (val === 'between') {
                    obj = createChildFilterObject(filter, convertDateRangeToString(dateRange), val);
                } else {
                    obj = createChildFilterObject(filter, date, val);
                }
            } else {
                obj = createChildFilterObject(filter, inputValue, val);
            }
            return { ...prevState, ...obj };
        });
    }

    const handleOnRowsScrollEnd = () => {
        if (initialLoad) return;

        if (filter) {
            if (tableData.rows.length < 1) {
                getDistinctColumnValues('', true, 1);
            }
            else if (tableData.rows.length + nextData.length !== count) {
                setPage(prevState => prevState++);
                getDistinctColumnValues('', true, page);
            }
        }
    };
    const convertDateRangeToString = (dateRange) => {
        const formattedStartDate = dayjs(dateRange[0]).format("YYYY-MM-DD");
        const formattedEndDate = dayjs(dateRange[1]).format("YYYY-MM-DD");
        const newDateRange = `${formattedStartDate},${formattedEndDate}`;
        return newDateRange;
    }

    const convertYearToDateString = (year) => `${year}-01-01`;

    const convertYearRangeToDateString = (startYear, endYear) => `${startYear}-01-01,${endYear}-01-01`;


    const renderDateContent = (dataType) => (
        <Paper elevation={0} sx={{
            p: 3,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[1]
        }}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                <Box sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                }}>
                    <CalendarTodayIcon sx={{ color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="subtitle1" fontWeight={500}>
                    {filter?.friendlyName}
                </Typography>
            </Box>

            <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, md: 4 }}>
                    <Select
                        fullWidth
                        id="date-operator-select"
                        onChange={(e) => handleOperatorChange(e.target.value, dataType)}
                        value={operatorValue}
                        size="small"
                        sx={{
                            height: 40,
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.divider
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.primary.main
                            }
                        }}
                    >
                        {Operators.number.map(({ name, label }) => (
                            <MenuItem value={name} key={name}>{label}</MenuItem>
                        ))}
                    </Select>
                </Grid>

                <Grid size={{ xs: 12, md: 8 }}>
                    {operatorValue === 'between' ? (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateRangePicker
                                value={dateRange}
                                onChange={(newValue) => {
                                    if (!newValue || newValue.length !== 2) {
                                        console.error('Invalid date range:', newValue);
                                        return;
                                    }
                                    setDateRange(newValue);
                                    if (newValue[0] && newValue[1]) {
                                        setGridSelection((prevState) => {
                                            const obj = createChildFilterObject(filter, convertDateRangeToString(newValue), operatorValue);
                                            return { ...prevState, ...obj };
                                        });
                                        setDateRange(newValue);
                                    } else {
                                        console.error('One or both dates are missing in the range');
                                    }
                                    validateDate(newValue);
                                }}
                                slotProps={{
                                    textField: (position) => ({
                                        size: 'small',
                                        fullWidth: true,
                                        placeholder: position === 'start' ? 'Start Date' : 'End Date',
                                        sx: {
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme.palette.divider
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme.palette.primary.main
                                            }
                                        },
                                        InputProps: {
                                            sx: { height: 40 },
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <CalendarTodayIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                                                </InputAdornment>
                                            ),
                                        },
                                    })
                                }}
                            />
                        </LocalizationProvider>
                    ) : (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={date}
                                onChange={(val) => {
                                    const newDate = dayjs(val).format("YYYY-MM-DD");
                                    setGridSelection((prevState) => {
                                        const obj = createChildFilterObject(filter, newDate, operatorValue);
                                        return { ...prevState, ...obj };
                                    });
                                    setDate(newDate);
                                    validateDate(newDate);
                                }}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: 'small',
                                        placeholder: 'Select date',

                                        sx: {
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme.palette.divider
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: theme.palette.primary.main
                                            }
                                        },

                                        slotProps: {
                                            input: {
                                                sx: { height: 40 },
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <CalendarTodayIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                                                    </InputAdornment>
                                                ),
                                            },
                                        },
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    )}
                </Grid>
            </Grid>
        </Paper>
    );

    const renderYearContent = (dataType) => (
        <Paper elevation={0} sx={{
            p: 3,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[1]
        }}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                <Box sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                }}>
                    <CalendarTodayIcon sx={{ color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="subtitle1" fontWeight={500}>
                    {filter?.friendlyName}
                </Typography>
            </Box>

            <Box sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.info.main, 0.08),
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
            }}>
                <Typography variant="caption" sx={{ color: theme.palette.info.dark, lineHeight: 1.5 }}>
                    Patients aged 4 or younger have a null birth year and will be excluded from results.
                    For patients older than 84, the birth year is capped at the current year minus 84.
                </Typography>
            </Box>

            <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, md: 4 }}>
                    <Select
                        fullWidth
                        id="year-operator-select"
                        onChange={(e) => handleOperatorChange(e.target.value, dataType)}
                        value={operatorValue}
                        size="small"
                        sx={{
                            height: 40,
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.divider
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.primary.main
                            }
                        }}
                    >
                        {Operators.birthYear.map(({ name, label }) => (
                            <MenuItem value={name} key={name}>{label}</MenuItem>
                        ))}
                    </Select>
                </Grid>

                <Grid size={{ xs: 12, md: 8 }}>
                    {operatorValue === 'between' ? (
                        <RangeInput
                            input={inputValue}
                            handleInputChange={(value) => {
                                const parts = value.split(',');
                                if (parts.length === 2 && parts[0] && parts[1]) {
                                    const dateRangeValue = convertYearRangeToDateString(parts[0], parts[1]);
                                    setInputValue(value);
                                    setGridSelection((prevState) => {
                                        const obj = createChildFilterObject(filter, dateRangeValue, operatorValue);
                                        return { ...prevState, ...obj };
                                    });
                                } else {
                                    setInputValue(value);
                                }
                            }}
                            startLabel="Start Year"
                            endLabel="End Year"
                            placeholder="YYYY"
                        />
                    ) : (
                        <TextField
                            fullWidth
                            label="Enter year (YYYY)"
                            variant="outlined"
                            size="small"
                            value={inputValue}
                            onChange={(e) => {
                                const year = e.target.value;
                                setInputValue(year);
                                if (year && year.length === 4) {
                                    const dateValue = convertYearToDateString(year);
                                    setGridSelection((prevState) => {
                                        const obj = createChildFilterObject(filter, dateValue, operatorValue);
                                        return { ...prevState, ...obj };
                                    });
                                }
                            }}
                            inputProps={{
                                maxLength: 4,
                                inputMode: 'numeric',
                                pattern: '[0-9]*'
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    height: 40
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.divider
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.primary.main
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <CalendarTodayIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}
                </Grid>
            </Grid>
        </Paper>
    );

    const renderNumberContent = (dataType) => (
        <Paper elevation={0} sx={{
            p: 3,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[1]
        }}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                <Box sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                }}>
                    <NumbersIcon sx={{ color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="subtitle1" fontWeight={500}>
                    {filter?.friendlyName}
                </Typography>
            </Box>

            <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, md: 4 }}>
                    <Select
                        fullWidth
                        id="number-operator-select"
                        onChange={(e) => handleOperatorChange(e.target.value, dataType)}
                        value={operatorValue}
                        size="small"
                        sx={{
                            height: 40,
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.divider
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.primary.main
                            }
                        }}
                    >
                        {Operators.number.map(({ name, label }) => (
                            <MenuItem value={name} key={name}>{label}</MenuItem>
                        ))}
                    </Select>
                </Grid>

                <Grid size={{ xs: 12, md: 8 }}>
                    {operatorValue === 'between' ? (
                        <RangeInput input={inputValue} handleInputChange={handleInputChange} />
                    ) : (
                        <TextField
                            fullWidth
                            label={filter.category === "Vital Sign History" ? endingLabel || 'Enter value' : 'Enter value'}
                            variant="outlined"
                            size="small"
                            value={inputValue}
                            onChange={(e) => handleInputChange(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    height: 40
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.divider
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.primary.main
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <NumbersIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}
                </Grid>
            </Grid>
        </Paper>
    );

    const renderTextContent = () => (
        <Paper elevation={0} sx={{
            p: 3,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[1]
        }}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                <Box sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2
                }}>
                    <TextFieldsIcon sx={{ color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="subtitle1" fontWeight={500}>
                    {filter?.friendlyName}
                </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
                <Search
                    getDistinctColumnValues={getDistinctColumnValues}
                    setPage={setPage}
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                />
            </Box>

            <Box sx={{ height: 400, width: '100%' }}>
                <DataGridPro
                    rows={tableData?.rows.concat(nextData)}
                    columns={headers}
                    loading={loading}
                    rowCount={count}
                    checkboxSelection
                    hideFooterPagination
                    rowSelectionModel={{ type: 'include', ids: new Set(selectedModel[filter.key] || []) }}
                    onRowsScrollEnd={() => handleOnRowsScrollEnd()}
                    onRowSelectionModelChange={(ids) => onRowsSelectionHandler(ids, filter.key)}
                    slots={{
                        loadingOverlay: LinearProgress,
                    }}
                    disableColumnMenu
                    sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#f9fafb',
                            borderBottom: `1px solid ${theme.palette.divider}`
                        },
                        '& .MuiDataGrid-cell:focus-within': {
                            outline: 'none',
                        },
                        '& .MuiDataGrid-row.Mui-selected': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                            },
                        },
                        '& .MuiCheckbox-root.Mui-checked': {
                            color: theme.palette.primary.main,
                        }
                    }}
                />
            </Box>
        </Paper>
    );

    function Content() {
        const { dataType } = filter.queryBuilderJSON[0];
        const evaluatedType = evaluateField(dataType);

        if (filter?.key === "BIRTH_YEAR") {
            return renderYearContent(dataType);
        }

        switch (evaluatedType) {
            case "DATE":
                return renderDateContent(dataType);
            case "NUMBER":
                return renderNumberContent(dataType);
            case "TEXT":
                return renderTextContent();
            default:
                return null;
        }
    }

    if (currentParentFilter?.RESULT_TYPE === 'N' && filter.queryBuilderJSON[0]?.dataType === 'TEXT') return null;
    if (currentParentFilter?.RESULT_TYPE === 'T' && filter.queryBuilderJSON[0]?.dataType === 'NUMBER') return null;

    return (
        <Fade in={true} timeout={300}>
            <Box id={`filter-${filter?.key}`}>
                {Content()}
            </Box>
        </Fade>
    );
}

export default ChildFilterWrapper;
